import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/api';
import { useUserStore } from '@/store/userStore';
import { Order, Folder } from '@/types';
import { cn } from '@/utils/cn';
import { getFileTypeFromOperation, getOperationDisplayName } from '@/utils/constants';
import { Button, Input, Modal } from '@/components/ui';
import { MediaDetailModal } from '@/components/shared';
import { notificationService } from '@/services/notificationService';
import { PlanModal, type PlanSeed } from '@/features/plan';
import {
  trackFolderCreated,
  trackFileRenamed,
  trackFileMoved,
  trackFileDeleted,
} from '@/services/analyticsService';
import {
  FolderOpen, Clock, Plus, RefreshCw, Play, Music, Image,
  Loader2, AlertCircle, ChevronDown, Calendar, Type, ArrowUpDown,
  MoreVertical, Edit2, Trash2, ChevronLeft, ChevronRight, CheckCircle,
  FolderPlus, MessageSquare, X
} from 'lucide-react';

const HEYGEN_CHAT_ORDER_TYPE = 'heygen_chat';

type SortOption = 'date' | 'name' | 'size' | 'type';

export function FilesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = useUserStore();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('recent');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const SORT_OPTIONS = [
    { value: 'date' as SortOption, label: t('files.sortByDate'), icon: Calendar },
    { value: 'name' as SortOption, label: t('files.sortByName'), icon: Type },
    { value: 'size' as SortOption, label: t('files.sortBySize'), icon: ArrowUpDown },
    { value: 'type' as SortOption, label: t('files.sortByType'), icon: FolderOpen },
  ];
  
  // Sort state
  const [sortOption, setSortOption] = useState<SortOption>('date');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Highlight state (for newly created files)
  const [highlightFirstItem, setHighlightFirstItem] = useState(false);
  
  // Folder scroll indicators
  const folderScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  // Modal states
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  
  // Folder context menu
  const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);
  const [showRenameFolder, setShowRenameFolder] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState<Folder | null>(null);
  const [newFolderRename, setNewFolderRename] = useState('');
  const [showDeleteFolder, setShowDeleteFolder] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<Folder | null>(null);
  
  // In-progress popup
  const [showInProgressPopup, setShowInProgressPopup] = useState(false);
  
  // Error popup
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorOrder, setErrorOrder] = useState<Order | null>(null);
  
  // Delete order confirmation
  const [showDeleteOrder, setShowDeleteOrder] = useState(false);
  const [deleteOrderTarget, setDeleteOrderTarget] = useState<Order | null>(null);
  
  // File context menu
  const [fileMenuOpen, setFileMenuOpen] = useState<string | null>(null);
  const [showMoveFile, setShowMoveFile] = useState(false);
  const [moveFileTarget, setMoveFileTarget] = useState<Order | null>(null);
  const [showRenameFile, setShowRenameFile] = useState(false);
  const [renameFileTarget, setRenameFileTarget] = useState<Order | null>(null);
  const [newFileName, setNewFileName] = useState('');
  
  // Media detail modal
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showMediaDetail, setShowMediaDetail] = useState(false);
  
  // Toast notification. Holds the active auto-dismiss timer in a ref so
  // consecutive showToast() calls don't race (new toast cancels the old timer).
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // v2 plan modal state — this page hosts the PlanModal so that the new
  // "Planning" order row is visible behind it as soon as the session starts.
  // Two entry points:
  //   - fresh plan from Home: ?newPlan=1 + seed in sessionStorage
  //   - resume an existing chat_active order: ?plan=<orderId> or card click
  const [planModal, setPlanModal] = useState<{
    open: boolean;
    seed?: PlanSeed;
    resumeOrderId?: string;
  }>({ open: false });

  // Show toast helper. `durationMs` lets callers override the default for
  // longer-visible warnings (e.g. the 3-active-plans cap uses 10s).
  const showToast = (
    message: string,
    type: 'success' | 'error' = 'success',
    durationMs = 3000,
  ) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  };

  const dismissToast = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  };

  // Clean up any pending timer on unmount.
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // Check for highlight param on mount - refresh orders and highlight newest
  useEffect(() => {
    if (searchParams.get('highlight') === 'true') {
      // Refresh orders to get the new one
      loadOrders();
      setHighlightFirstItem(true);
      // Remove highlight after animation
      const timer = setTimeout(() => {
        setHighlightFirstItem(false);
        // Clean up URL
        navigate('/files', { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  // v2: auto-open PlanModal when arriving with ?newPlan=1 (fresh plan from
  // Home) or ?plan=<orderId> (resume an in-flight plan from a card click).
  //
  // We guard with a ref because React 18 Strict Mode re-runs effects on
  // mount: without the guard, the second pass sees an empty sessionStorage
  // (we cleared it on the first pass) and opens the modal again with an
  // undefined seed — which would immediately fail with "Couldn't start the
  // plan session" AND leak a second backend order.
  const planParamsHandledRef = useRef(false);
  useEffect(() => {
    if (planParamsHandledRef.current) return;
    const newPlan = searchParams.get('newPlan');
    const resumeOrderId = searchParams.get('plan');
    if (newPlan !== '1' && !resumeOrderId) return;
    planParamsHandledRef.current = true;

    if (newPlan === '1') {
      let seed: PlanSeed | undefined;
      try {
        const raw = sessionStorage.getItem('vivideo_plan_seed');
        if (raw) seed = JSON.parse(raw) as PlanSeed;
        sessionStorage.removeItem('vivideo_plan_seed');
      } catch { /* ignore */ }
      if (!seed) {
        // Arrived at /files?newPlan=1 without a seed (e.g. direct reload).
        // Just drop the param and do nothing; user can kick off from Home.
        const next = new URLSearchParams(searchParams);
        next.delete('newPlan');
        navigate(`/files${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
        return;
      }
      setPlanModal({ open: true, seed });
    } else if (resumeOrderId) {
      setPlanModal({ open: true, resumeOrderId });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('newPlan');
    next.delete('plan');
    navigate(`/files${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  
  // Update scroll indicators
  useEffect(() => {
    const updateArrows = () => {
      if (folderScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = folderScrollRef.current;
        setShowLeftArrow(scrollLeft > 10);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };
    
    updateArrows();
    const el = folderScrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateArrows);
      return () => el.removeEventListener('scroll', updateArrows);
    }
  }, [folders]);
  
  // Request notification permission when user visits files page
  useEffect(() => {
    if (userId && !notificationService.hasAskedPermission()) {
      console.log('[FilesPage] Requesting notification permission...');
      notificationService.requestPermissionAndRegister(userId).then((result) => {
        console.log('[FilesPage] Notification permission result:', result);
      }).catch((err) => {
        console.error('[FilesPage] Notification permission error:', err);
      });
    }
  }, [userId]);

  // Load data
  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadOrders();
    }
  }, [selectedFolderId, userId]);

  const loadData = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const [foldersData] = await Promise.all([
        apiService.getUserFolders(userId),
      ]);
      setFolders(foldersData);
      await loadOrders();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!userId) return;
    try {
      const folderId = selectedFolderId === 'recent' ? userId : selectedFolderId;
      const ordersData = await apiService.getUserOrders(folderId, userId);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
    showToast(t('success.filesRefreshed'), 'success');
  };

  const handleCreateFolder = async () => {
    if (!userId || !newFolderName.trim()) return;
    setIsCreatingFolder(true);
    try {
      await apiService.createFolder(newFolderName.trim(), userId);
      trackFolderCreated();
      setNewFolderName('');
      setShowCreateFolder(false);
      await loadData();
      showToast(t('common.success'), 'success');
    } catch (error) {
      console.error('Error creating folder:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setIsCreatingFolder(false);
    }
  };
  
  const handleRenameFolder = async () => {
    if (!renameFolderTarget || !newFolderRename.trim()) return;
    try {
      setShowRenameFolder(false);
      setRenameFolderTarget(null);
      setNewFolderRename('');
      showToast(t('misc.comingSoon'), 'success');
    } catch (error) {
      console.error('Error renaming folder:', error);
      showToast(t('common.error'), 'error');
    }
  };
  
  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      setShowDeleteFolder(false);
      setDeleteFolderTarget(null);
      await loadData();
      showToast(t('misc.comingSoon'), 'success');
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToast(t('common.error'), 'error');
    }
  };
  
  // State for delete operation loading
  const [isDeletingOrder, setIsDeletingOrder] = useState(false);
  
  const handleDeleteOrder = async () => {
    if (!deleteOrderTarget) return;
    
    setIsDeletingOrder(true);
    
    try {
      await apiService.deleteOrder(deleteOrderTarget.orderId);
      trackFileDeleted(deleteOrderTarget.operationType);
      
      setShowDeleteOrder(false);
      setDeleteOrderTarget(null);
      await loadOrders();
      showToast(t('common.success'), 'success');
    } catch (error) {
      console.error('Error deleting order:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setIsDeletingOrder(false);
    }
  };
  
  // State for rename operation loading
  const [isRenamingFile, setIsRenamingFile] = useState(false);
  
  const handleRenameFile = async () => {
    if (!renameFileTarget || !newFileName.trim()) return;
    
    setIsRenamingFile(true);
    
    try {
      await apiService.renameOrder(renameFileTarget.orderId, newFileName.trim());
      trackFileRenamed();
      
      setShowRenameFile(false);
      setRenameFileTarget(null);
      setNewFileName('');
      await loadOrders();
      showToast(t('common.success'), 'success');
    } catch (error) {
      console.error('Error renaming file:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setIsRenamingFile(false);
    }
  };
  
  // State for move operation loading
  const [isMovingFile, setIsMovingFile] = useState(false);
  const [movingToFolderId, setMovingToFolderId] = useState<string | null>(null);
  
  const handleMoveFile = async (targetFolderId: string) => {
    if (!moveFileTarget) return;
    
    setIsMovingFile(true);
    setMovingToFolderId(targetFolderId);
    
    try {
      await apiService.moveOrder(moveFileTarget.orderId, targetFolderId);
      trackFileMoved();
      
      // Find folder name for success message
      const targetFolder = folders.find(f => f.folderId === targetFolderId);
      const folderName = targetFolder?.folderName || 'folder';
      
      setShowMoveFile(false);
      setMoveFileTarget(null);
      await loadOrders();
      showToast(`${t('common.success')} - ${folderName}`, 'success');
    } catch (error) {
      console.error('Error moving file:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setIsMovingFile(false);
      setMovingToFolderId(null);
    }
  };
  
  
  const scrollFolders = (direction: 'left' | 'right') => {
    if (folderScrollRef.current) {
      const scrollAmount = 150;
      folderScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleOrderClick = (order: Order) => {
    // v2 HeyGen chat sessions:
    //   - chat_active (plan session, user hasn't approved yet) → reopen PlanModal on Home
    //   - in_progress (user approved, HeyGen is rendering)     → generic in-progress popup
    //   - completed                                            → standard play/download
    //   - failed                                               → standard error popup
    if (order.operationType === HEYGEN_CHAT_ORDER_TYPE) {
      const rawStatus = (order.orderStatus as string) || '';
      const status = rawStatus.toLowerCase();
      if (status === 'chat_active' || status === 'planning' || status === 'pending') {
        // v2: resume the plan inline on this page — no cross-page navigation.
        setPlanModal({ open: true, resumeOrderId: order.orderId });
        return;
      }
      if (status === 'failed') {
        setErrorOrder(order);
        setShowErrorPopup(true);
        return;
      }
      if (status === 'completed' && (order.videoUrl || order.outputUrl)) {
        setSelectedOrder(order);
        setShowMediaDetail(true);
        return;
      }
      // Approved + rendering (or any other non-terminal state)
      setShowInProgressPopup(true);
      return;
    }
    if (order.orderStatus === 'in_progress') {
      setShowInProgressPopup(true);
    } else if (order.orderStatus === 'preview' || order.isPreview) {
      setSelectedOrder(order);
      setShowMediaDetail(true);
    } else if (order.orderStatus === 'failed') {
      setErrorOrder(order);
      setShowErrorPopup(true);
    } else if (order.orderStatus === 'completed' && (order.videoUrl || order.outputUrl)) {
      setSelectedOrder(order);
      setShowMediaDetail(true);
    }
  };

  const handlePlayChatVideo = (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    if (order.outputUrl || order.videoUrl) {
      setSelectedOrder(order);
      setShowMediaDetail(true);
    }
  };

  const getStatusIcon = (order: Order) => {
    // v2 chat session item
    if (order.operationType === HEYGEN_CHAT_ORDER_TYPE) {
      if (order.orderStatus === 'failed') {
        return <AlertCircle className="w-5 h-5 text-status-error" />;
      }
      if (order.orderStatus === 'completed' && (order.outputUrl || order.videoUrl)) {
        return <Play className="w-5 h-5 text-primary" />;
      }
      return <MessageSquare className="w-5 h-5 text-primary" />;
    }
    if (order.orderStatus === 'in_progress') {
      return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
    }
    if (order.orderStatus === 'failed') {
      return <AlertCircle className="w-5 h-5 text-status-error" />;
    }
    const fileType = getFileTypeFromOperation(order.operationType);
    if (fileType === 'audio') return <Music className="w-5 h-5 text-accent-green" />;
    if (fileType === 'image') return <Image className="w-5 h-5 text-accent-orange" />;
    // Preview orders and completed videos both show play icon
    return <Play className="w-5 h-5 text-primary" />;
  };

  // Sort orders based on selected option
  const sortedOrders = [...orders].sort((a, b) => {
    // In-progress always first
    if (a.orderStatus === 'in_progress' && b.orderStatus !== 'in_progress') return -1;
    if (b.orderStatus === 'in_progress' && a.orderStatus !== 'in_progress') return 1;
    
    switch (sortOption) {
      case 'name':
        return a.orderName.localeCompare(b.orderName);
      case 'size':
        return new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime();
      case 'type':
        return a.operationType.localeCompare(b.operationType);
      case 'date':
      default:
    return new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime();
    }
  });

  const currentSortOption = SORT_OPTIONS.find(o => o.value === sortOption) || SORT_OPTIONS[0];

  // Stuck `in_progress` orders older than 1 hour (per the user's local clock)
  // get an always-visible trash button so they can be cleaned up — the 3-dot
  // menu only appears for completed/preview/chat rows, so without this an
  // in_progress row that never finishes has no way to be deleted from the UI.
  const ONE_HOUR_MS = 60 * 60 * 1000;
  const isOrderDeletableByAge = (order: Order) => {
    if (order.orderStatus !== 'in_progress') return false;
    const created = new Date(order.orderDate).getTime();
    if (Number.isNaN(created)) return false;
    return Date.now() - created > ONE_HOUR_MS;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {/* Toast Notification */}
      {toast && (
        <div className={cn(
          "fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-slide-down max-w-md",
          toast.type === 'success'
            ? "bg-accent-green text-white"
            : "bg-status-error text-white"
        )}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm font-medium flex-1">{toast.message}</span>
          <button
            type="button"
            onClick={dismissToast}
            className="ml-1 -mr-1 rounded-md hover:bg-white/15 p-1 focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">{t('files.title')}</h1>
        <div className="flex items-center gap-3">
          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-surface border border-glass-border hover:border-primary/30 transition-all"
            >
              <currentSortOption.icon className="w-4 h-4 text-text-secondary" />
              <span className="text-sm text-text-primary">{currentSortOption.label}</span>
              <ChevronDown className={cn(
                "w-4 h-4 text-text-tertiary transition-transform",
                showSortDropdown && "rotate-180"
              )} />
            </button>
            
            {showSortDropdown && (
              <div className="absolute right-0 top-full mt-2 w-40 py-2 bg-surface border border-glass-border rounded-xl shadow-xl z-50">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortOption(option.value);
                      setShowSortDropdown(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors",
                      sortOption === option.value && "bg-primary/10 text-primary"
                    )}
                  >
                    <option.icon className="w-4 h-4" />
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

        <Button
          variant="ghost"
          onClick={handleRefresh}
          isLoading={isRefreshing}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          {t('common.refresh')}
        </Button>
      </div>
      </div>

      {/* Folders with scroll indicators */}
      <div className="relative mb-6">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scrollFolders('left')}
            className="absolute left-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-gradient-to-r from-background to-transparent"
          >
            <div className="p-1 rounded bg-surface-light/80">
              <ChevronLeft className="w-4 h-4 text-text-secondary" />
            </div>
          </button>
        )}
        
        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scrollFolders('right')}
            className="absolute right-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-gradient-to-l from-background to-transparent"
          >
            <div className="p-1 rounded bg-surface-light/80">
              <ChevronRight className="w-4 h-4 text-text-secondary" />
            </div>
          </button>
        )}
        
        <div 
          ref={folderScrollRef}
          className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide"
        >
        {/* Recent (pinned) */}
        <button
          onClick={() => setSelectedFolderId('recent')}
          className={cn(
            'flex-shrink-0 flex flex-col items-center p-4 rounded-xl border-2 transition-all min-w-[100px]',
            selectedFolderId === 'recent'
              ? 'bg-primary/10 border-primary text-primary'
              : 'bg-surface border-glass-border text-text-secondary hover:border-primary/30'
          )}
        >
          <Clock className="w-6 h-6 mb-2" />
          <span className="text-sm font-medium">{t('files.recent')}</span>
        </button>

        {/* User folders */}
        {folders.map((folder) => (
            <div key={folder.folderId} className="relative flex-shrink-0">
          <button
            onClick={() => setSelectedFolderId(folder.folderId)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setFolderMenuOpen(folder.folderId);
                }}
            className={cn(
                  'flex flex-col items-center p-4 rounded-xl border-2 transition-all min-w-[100px]',
              selectedFolderId === folder.folderId
                ? 'bg-primary/10 border-primary text-primary'
                : 'bg-surface border-glass-border text-text-secondary hover:border-primary/30'
            )}
          >
            <FolderOpen className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium truncate max-w-[80px]">{folder.folderName}</span>
          </button>
              
              {/* Folder menu button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFolderMenuOpen(folderMenuOpen === folder.folderId ? null : folder.folderId);
                }}
                className="absolute top-1 right-1 p-1 rounded-full hover:bg-surface-light transition-colors opacity-0 group-hover:opacity-100"
              >
                <MoreVertical className="w-3 h-3 text-text-tertiary" />
              </button>
              
              {/* Folder context menu */}
              {folderMenuOpen === folder.folderId && (
                <div className="absolute top-full left-0 mt-1 w-32 py-1 bg-surface border border-glass-border rounded-lg shadow-xl z-50">
                  <button
                    onClick={() => {
                      setRenameFolderTarget(folder);
                      setNewFolderRename(folder.folderName);
                      setShowRenameFolder(true);
                      setFolderMenuOpen(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-light"
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('common.rename')}
                  </button>
                  <button
                    onClick={() => {
                      setDeleteFolderTarget(folder);
                      setShowDeleteFolder(true);
                      setFolderMenuOpen(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-status-error hover:bg-surface-light"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.delete')}
                  </button>
                </div>
              )}
            </div>
        ))}

        {/* Create folder button */}
        <button
          onClick={() => setShowCreateFolder(true)}
          className="flex-shrink-0 flex flex-col items-center p-4 rounded-xl border-2 border-dashed border-glass-border text-text-tertiary hover:border-primary hover:text-primary transition-all min-w-[100px]"
        >
          <Plus className="w-6 h-6 mb-2" />
          <span className="text-sm font-medium">{t('files.createFolder')}</span>
        </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {sortedOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FolderOpen className="w-16 h-16 text-text-tertiary mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">{t('files.noFilesYet')}</h3>
            <p className="text-text-secondary mb-4">{t('files.createFirstVideoToSeeHere')}</p>
            <Button onClick={() => navigate('/')}>
              {t('home.createVideo')}
            </Button>
          </div>
        ) : (
          sortedOrders.map((order, index) => (
            <div
              key={order.orderId}
              className={cn(
                'flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer relative group',
                order.orderStatus === 'failed'
                  ? 'bg-status-error/5 border-status-error/30 hover:bg-status-error/10'
                  : 'bg-surface border-glass-border hover:border-primary/30',
                index === 0 && highlightFirstItem && 'animate-pulse-glow ring-2 ring-primary ring-offset-2 ring-offset-background'
              )}
              onClick={() => handleOrderClick(order)}
            >
              {/* Thumbnail/Status - Clickable icon area */}
              <div 
                className={cn(
                  'w-16 h-16 rounded-xl flex items-center justify-center cursor-pointer touch-manipulation overflow-hidden relative',
                  order.orderStatus === 'failed' ? 'bg-status-error/20' : 'bg-primary/20'
                )}
                onClick={(e) => {
                  // Ensure click on icon opens the video, prevent bubbling to parent
                  e.stopPropagation();
                  handleOrderClick(order);
                }}
                onTouchEnd={(e) => {
                  // Handle touch events for mobile
                  e.stopPropagation();
                  handleOrderClick(order);
                }}
              >
                {/* Show preview image as thumbnail for preview orders (same as completed videos) */}
                {(order.orderStatus === 'preview' || order.isPreview) && order.previewImageUrl ? (
                  <>
                    <img 
                      src={order.previewImageUrl} 
                      alt="Preview" 
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: 'blur(3px)' }}
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                  </>
                ) : order.operationType === HEYGEN_CHAT_ORDER_TYPE && order.orderStatus === 'completed' && (order.outputUrl || order.videoUrl) ? (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/5 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-primary/60" />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handlePlayChatVideo(order, e)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors"
                      aria-label="Play video"
                    >
                      <Play className="w-6 h-6 text-white fill-white" />
                    </button>
                  </>
                ) : (
                  getStatusIcon(order)
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-text-primary truncate">{order.orderName}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-text-tertiary">
                    {order.operationType === HEYGEN_CHAT_ORDER_TYPE
                      ? t('files.planSession')
                      : getOperationDisplayName(order.operationType)}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {new Date(order.orderDate).toLocaleDateString()}
                  </span>
                </div>
                {order.operationType === HEYGEN_CHAT_ORDER_TYPE && order.orderStatus !== 'completed' && order.orderStatus !== 'failed' && (
                  <p className="text-xs text-primary mt-1">{t('files.planOpenToContinue')}</p>
                )}
                {order.operationType !== HEYGEN_CHAT_ORDER_TYPE && order.orderStatus === 'in_progress' && (
                  <p className="text-xs text-primary mt-1">{t('common.processing')}</p>
                )}
                {order.orderStatus === 'failed' && (
                  <p className="text-xs text-status-error mt-1 truncate">
                    {order.errorMessage || t('errors.generationFailed')}
                  </p>
                )}
              </div>

              {/* Status Badge */}
              <div className={cn(
                'px-3 py-1 rounded-full text-xs font-medium',
                {
                  'bg-primary/20 text-primary': order.orderStatus === 'in_progress' || (order.operationType === HEYGEN_CHAT_ORDER_TYPE && order.orderStatus !== 'completed' && order.orderStatus !== 'failed'),
                  'bg-accent-green/20 text-accent-green': order.orderStatus === 'completed' || order.orderStatus === 'preview' || order.isPreview,
                  'bg-status-error/20 text-status-error': order.orderStatus === 'failed',
                }
              )}>
                {order.operationType === HEYGEN_CHAT_ORDER_TYPE && order.orderStatus !== 'completed' && order.orderStatus !== 'failed'
                  ? t('files.planBadge')
                  : order.orderStatus === 'in_progress' ? t('common.processing') :
                    order.orderStatus === 'failed' ? t('status.failed') :
                    t('common.success')}
              </div>
              
              {/* Age-based delete button: in_progress orders older than 1
                  hour (per user's local clock) get a visible trash button so
                  stuck rows can be removed — the 3-dot menu only appears
                  for completed/preview/chat rows. */}
              {isOrderDeletableByAge(order) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteOrderTarget(order);
                    setShowDeleteOrder(true);
                  }}
                  className="p-2 rounded-lg text-status-error hover:bg-status-error/10 transition-colors"
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}

              {/* File Menu Button - for completed, preview, and v2 chat files */}
              {(order.orderStatus === 'completed' || order.orderStatus === 'preview' || order.isPreview || order.operationType === HEYGEN_CHAT_ORDER_TYPE) && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFileMenuOpen(fileMenuOpen === order.orderId ? null : order.orderId);
                    }}
                    className="p-2 rounded-lg hover:bg-surface-light transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <MoreVertical className="w-5 h-5 text-text-tertiary" />
                  </button>
                  
                  {/* File context menu */}
                  {fileMenuOpen === order.orderId && (
                    <div className="absolute right-0 top-full mt-1 w-44 py-2 bg-surface border border-glass-border rounded-xl shadow-xl z-50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameFileTarget(order);
                          setNewFileName(order.orderName);
                          setShowRenameFile(true);
                          setFileMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-light transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        {t('common.rename')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMoveFileTarget(order);
                          setShowMoveFile(true);
                          setFileMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-light transition-colors"
                      >
                        <FolderPlus className="w-4 h-4" />
                        {t('common.move')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteOrderTarget(order);
                          setShowDeleteOrder(true);
                          setFileMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-status-error hover:bg-surface-light transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        title={t('files.createFolder')}
      >
        <div className="space-y-4">
          <Input
            label={t('files.folderName')}
            placeholder={t('files.folderName')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowCreateFolder(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateFolder} isLoading={isCreatingFolder}>
              {t('common.create')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Rename Folder Modal */}
      <Modal
        isOpen={showRenameFolder}
        onClose={() => {
          setShowRenameFolder(false);
          setRenameFolderTarget(null);
        }}
        title={t('files.renameFolder')}
      >
        <div className="space-y-4">
          <Input
            label={t('files.folderName')}
            placeholder={t('files.folderName')}
            value={newFolderRename}
            onChange={(e) => setNewFolderRename(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
          />
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowRenameFolder(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRenameFolder}>
              {t('common.rename')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Delete Folder Modal */}
      <Modal
        isOpen={showDeleteFolder}
        onClose={() => {
          setShowDeleteFolder(false);
          setDeleteFolderTarget(null);
        }}
        title={t('files.deleteFolder')}
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            {t('common.confirm')} "{deleteFolderTarget?.folderName}"?
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteFolder(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteFolder}
              className="bg-status-error hover:bg-status-error/90"
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* In-Progress Popup */}
      <Modal
        isOpen={showInProgressPopup}
        onClose={() => setShowInProgressPopup(false)}
        title={t('status.videoGenerating')}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
            <div>
              <p className="text-text-primary font-medium">{t('status.creatingYourVideo')}</p>
              <p className="text-sm text-text-secondary">{t('status.videoBeingGenerated')}</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowInProgressPopup(false)}>
              {t('common.ok')}
            </Button>
          </div>
        </div>
      </Modal>
      
      
      {/* Error Popup */}
      <Modal
        isOpen={showErrorPopup}
        onClose={() => {
          setShowErrorPopup(false);
          setErrorOrder(null);
        }}
        title={t('errors.generationFailed')}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-status-error/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-status-error" />
            </div>
            <div>
              <p className="text-text-primary">
                {t('errors.generationFailedMessage')} "{errorOrder?.orderName}"
              </p>
            </div>
          </div>
          
          {/* Suggestion box */}
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
            <p className="text-sm font-medium text-text-primary mb-1">
              {t('errors.tryDifferentModel')}
            </p>
            <p className="text-xs text-text-secondary">
              {t('errors.tryDifferentModelHint')}
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              onClick={() => {
                setDeleteOrderTarget(errorOrder);
                setShowDeleteOrder(true);
                setShowErrorPopup(false);
              }}
              variant="ghost"
              className="text-status-error hover:bg-status-error/10"
            >
              {t('common.delete')}
            </Button>
            <Button onClick={() => setShowErrorPopup(false)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Delete Order Modal */}
      <Modal
        isOpen={showDeleteOrder}
        onClose={() => {
          if (!isDeletingOrder) {
            setShowDeleteOrder(false);
            setDeleteOrderTarget(null);
          }
        }}
        title={t('files.deleteFile')}
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            {t('common.confirm')} "{deleteOrderTarget?.orderName}"?
          </p>
          <div className="flex justify-end gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setShowDeleteOrder(false)}
              disabled={isDeletingOrder}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteOrder}
              className="bg-status-error hover:bg-status-error/90"
              isLoading={isDeletingOrder}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Rename File Modal */}
      <Modal
        isOpen={showRenameFile}
        onClose={() => {
          if (!isRenamingFile) {
            setShowRenameFile(false);
            setRenameFileTarget(null);
            setNewFileName('');
          }
        }}
        title={t('files.renameFile')}
      >
        <div className="space-y-4">
          <Input
            label={t('files.fileName')}
            placeholder={t('files.fileName')}
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !isRenamingFile && handleRenameFile()}
            disabled={isRenamingFile}
          />
          <div className="flex justify-end gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setShowRenameFile(false)}
              disabled={isRenamingFile}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRenameFile}
              isLoading={isRenamingFile}
            >
              {t('common.rename')}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Move File Modal */}
      <Modal
        isOpen={showMoveFile}
        onClose={() => {
          if (!isMovingFile) {
            setShowMoveFile(false);
            setMoveFileTarget(null);
          }
        }}
        title={t('files.moveFile')}
      >
        <div className="space-y-4">
          <p className="text-text-secondary mb-4">
            {t('files.selectDestinationFolder')} "{moveFileTarget?.orderName}":
          </p>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {folders.length === 0 ? (
              <p className="text-text-tertiary text-center py-4">
                {t('files.createFolder')}
              </p>
            ) : (
              folders.map((folder) => {
                const isFolderLoading = isMovingFile && movingToFolderId === folder.folderId;
                return (
                  <button
                    key={folder.folderId}
                    onClick={() => !isMovingFile && handleMoveFile(folder.folderId)}
                    disabled={isMovingFile}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left",
                      isMovingFile ? "opacity-70 cursor-not-allowed" : "hover:bg-surface-light"
                    )}
                  >
                    {isFolderLoading ? (
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                      <FolderOpen className="w-5 h-5 text-primary" />
                    )}
                    <span className="text-text-primary">{folder.folderName}</span>
                  </button>
                );
              })
            )}
          </div>
          <div className="flex justify-end">
            <Button 
              variant="ghost" 
              onClick={() => setShowMoveFile(false)}
              disabled={isMovingFile}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Media Detail Modal */}
      <MediaDetailModal
        isOpen={showMediaDetail}
        onClose={() => {
          setShowMediaDetail(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />

      {/* v2 Plan Modal — hosted here so the Planning row is visible behind it. */}
      <PlanModal
        isOpen={planModal.open}
        onClose={() => setPlanModal({ open: false })}
        onApproved={() => {
          setPlanModal({ open: false });
          try { localStorage.removeItem('vivideo_agent_prompt'); } catch { /* ignore */ }
          loadOrders();
          setHighlightFirstItem(true);
          setTimeout(() => setHighlightFirstItem(false), 2000);
        }}
        onTooManyPlans={(limit) => {
          // Backend rejected because user is already at the active-plan cap.
          // Drop the seed so a refresh doesn't retry, and nudge the user to
          // the existing planning rows (visible right below). Warning stays
          // on screen for 10s so slower readers catch it; dismissible via X.
          try { sessionStorage.removeItem('vivideo_plan_seed'); } catch { /* ignore */ }
          showToast(t('plan.errors.tooManyActivePlans', { limit }), 'error', 10000);
        }}
        seed={planModal.seed}
        resumeOrderId={planModal.resumeOrderId}
      />
      
      {/* Click outside to close dropdowns */}
      {(showSortDropdown || folderMenuOpen || fileMenuOpen) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowSortDropdown(false);
            setFolderMenuOpen(null);
            setFileMenuOpen(null);
          }}
        />
      )}
    </div>
  );
}
